import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import {
  FileText,
  Download,
  Eye,
  Lock,
  Users,
  TrendingUp,
  DollarSign,
  BarChart3,
  Calendar,
  Building2,
  Shield,
  Globe,
  Target,
  Zap,
  ExternalLink,
  Mail,
  MessageSquare,
  Loader2,
  Printer
} from 'lucide-react';

const Dataroom = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Company metrics
  const metrics = {
    totalUsers: 523,
    activeUsers: 412,
    monthlyGrowth: 18.5,
    totalForms: 2847,
    totalSubmissions: 156420,
    mrr: 12500,
    arr: 150000,
    churnRate: 2.3,
    avgRevenuePerUser: 24,
    countries: 1,
    teamSize: 5
  };

  // Key documents (simulated - in production would come from storage)
  const documents = [
    {
      category: 'Company Overview',
      items: [
        { name: 'Company Pitch Deck', type: 'PDF', size: '4.2 MB', date: '2025-01-15', restricted: false },
        { name: 'Executive Summary', type: 'PDF', size: '1.1 MB', date: '2025-01-10', restricted: false },
        { name: 'Product Demo Video', type: 'MP4', size: '45 MB', date: '2025-01-12', restricted: false }
      ]
    },
    {
      category: 'Financial Information',
      items: [
        { name: 'Financial Projections 2025-2027', type: 'XLSX', size: '2.3 MB', date: '2025-01-08', restricted: true },
        { name: 'Revenue Breakdown', type: 'PDF', size: '890 KB', date: '2025-01-05', restricted: true },
        { name: 'Unit Economics Analysis', type: 'PDF', size: '1.5 MB', date: '2025-01-03', restricted: true }
      ]
    },
    {
      category: 'Product & Technology',
      items: [
        { name: 'Technical Architecture', type: 'PDF', size: '3.1 MB', date: '2024-12-28', restricted: false },
        { name: 'Product Roadmap 2025', type: 'PDF', size: '2.8 MB', date: '2025-01-01', restricted: false },
        { name: 'API Documentation', type: 'PDF', size: '1.2 MB', date: '2024-12-20', restricted: false }
      ]
    },
    {
      category: 'Market & Competition',
      items: [
        { name: 'Market Analysis Report', type: 'PDF', size: '5.4 MB', date: '2024-12-15', restricted: false },
        { name: 'Competitive Landscape', type: 'PDF', size: '2.1 MB', date: '2024-12-10', restricted: false },
        { name: 'Target Market Demographics', type: 'PDF', size: '1.8 MB', date: '2024-12-05', restricted: false }
      ]
    },
    {
      category: 'Legal & Compliance',
      items: [
        { name: 'Terms of Service', type: 'PDF', size: '340 KB', date: '2024-11-20', restricted: false },
        { name: 'Privacy Policy', type: 'PDF', size: '280 KB', date: '2024-11-20', restricted: false },
        { name: 'Data Processing Agreement', type: 'PDF', size: '420 KB', date: '2024-11-15', restricted: true }
      ]
    }
  ];

  const fundingDetails = {
    seeking: 500000,
    type: 'Pre-Seed',
    useOfFunds: [
      { category: 'Product Development', percentage: 40, amount: 200000 },
      { category: 'Sales & Marketing', percentage: 30, amount: 150000 },
      { category: 'Team Expansion', percentage: 20, amount: 100000 },
      { category: 'Operations', percentage: 10, amount: 50000 }
    ],
    milestones: [
      { target: '5,000 Active Users', timeline: '6 months', status: 'upcoming' },
      { target: 'Pan-African Expansion (5 countries)', timeline: '12 months', status: 'upcoming' },
      { target: '$50K MRR', timeline: '12 months', status: 'upcoming' },
      { target: 'Multi-channel Integration', timeline: '9 months', status: 'upcoming' }
    ]
  };

  const generatePitchDeckPDF = async () => {
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPos = 20;
      
      const addPage = () => {
        pdf.addPage();
        yPos = 20;
      };
      
      const addTitle = (text: string, size: number = 24) => {
        pdf.setFontSize(size);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(99, 102, 241); // Primary color
        pdf.text(text, pageWidth / 2, yPos, { align: 'center' });
        yPos += size * 0.5;
      };
      
      const addSubtitle = (text: string, size: number = 14) => {
        pdf.setFontSize(size);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(text, pageWidth / 2, yPos, { align: 'center' });
        yPos += size * 0.6;
      };
      
      const addText = (text: string, size: number = 11, align: 'left' | 'center' | 'right' = 'left') => {
        pdf.setFontSize(size);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        const lines = pdf.splitTextToSize(text, pageWidth - 40);
        pdf.text(lines, align === 'center' ? pageWidth / 2 : 20, yPos, { align });
        yPos += lines.length * size * 0.5;
      };
      
      const addMetricBox = (label: string, value: string, x: number, y: number, width: number) => {
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(x, y, width, 25, 3, 3, 'F');
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(label, x + width / 2, y + 8, { align: 'center' });
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(99, 102, 241);
        pdf.text(value, x + width / 2, y + 19, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
      };
      
      // ===== COVER PAGE =====
      yPos = 60;
      addTitle('Bravax Forms', 36);
      yPos += 10;
      addSubtitle('Investor Pitch Deck', 18);
      yPos += 30;
      addText('Powering Data-Driven Growth for African SMBs', 14, 'center');
      yPos += 40;
      
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Confidential', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
      pdf.text(new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }), pageWidth / 2, yPos, { align: 'center' });
      
      // ===== THE PROBLEM =====
      addPage();
      addTitle('The Problem', 24);
      yPos += 15;
      addText('African SMBs lose significant revenue due to disconnected tools and manual data collection processes.', 12);
      yPos += 10;
      addText('• Disconnected manual processes lead to errors and missed leads', 11);
      yPos += 6;
      addText('• No centralized system to track customer inquiries', 11);
      yPos += 6;
      addText('• Lost revenue due to slow response times', 11);
      yPos += 6;
      addText('• Poor customer experience and follow-up', 11);
      yPos += 20;
      
      pdf.setFillColor(254, 243, 199);
      pdf.roundedRect(20, yPos, pageWidth - 40, 30, 3, 3, 'F');
      pdf.setFontSize(11);
      pdf.setTextColor(146, 64, 14);
      pdf.text('33M+ small businesses in Nigeria alone struggle with digital order management', pageWidth / 2, yPos + 18, { align: 'center' });
      
      // ===== THE SOLUTION =====
      addPage();
      addTitle('Our Solution', 24);
      yPos += 15;
      addText('Bravax Forms is an AI-powered form builder and CRM that enables small businesses to capture leads and manage customer relationships effortlessly.', 12);
      yPos += 15;
      addText('Key Features:', 14);
      yPos += 8;
      addText('• Drag-and-drop form builder - no coding required', 11);
      yPos += 6;
      addText('• Smart delivery channels (including WhatsApp)', 11);
      addText('• Integrated CRM for customer management', 11);
      yPos += 6;
      addText('• Analytics dashboard for business insights', 11);
      yPos += 6;
      addText('• Intelligent automation templates', 11);
      yPos += 6;
      addText('• API access for developers', 11);
      
      // ===== TRACTION & METRICS =====
      addPage();
      addTitle('Traction & Key Metrics', 24);
      yPos += 20;
      
      addMetricBox('Active Users', metrics.activeUsers.toString(), 20, yPos, 50);
      addMetricBox('MRR', `$${metrics.mrr.toLocaleString()}`, 75, yPos, 50);
      addMetricBox('Forms Created', metrics.totalForms.toLocaleString(), 130, yPos, 55);
      yPos += 35;
      
      addMetricBox('Submissions', metrics.totalSubmissions.toLocaleString(), 20, yPos, 55);
      addMetricBox('Churn Rate', `${metrics.churnRate}%`, 80, yPos, 45);
      addMetricBox('Growth Rate', `+${metrics.monthlyGrowth}%`, 130, yPos, 55);
      yPos += 45;
      
      addText('Key Highlights:', 14);
      yPos += 8;
      addText('• 18.5% month-over-month user growth', 11);
      yPos += 6;
      addText('• 22% month-over-month revenue growth', 11);
      yPos += 6;
      addText('• Net Promoter Score of 72 (Excellent)', 11);
      yPos += 6;
      addText('• Customer Lifetime Value of $288', 11);
      
      // ===== MARKET OPPORTUNITY =====
      addPage();
      addTitle('Market Opportunity', 24);
      yPos += 20;
      
      addMetricBox('TAM', '$12B', 20, yPos, 55);
      addMetricBox('SAM', '$2.4B', 77.5, yPos, 55);
      addMetricBox('SOM', '$180M', 135, yPos, 55);
      yPos += 40;
      
      addText('African SMB Software Market', 12);
      yPos += 5;
      addText('Social commerce is the fastest growing sector in African tech, yet most SMBs lack the tools to manage it.', 11);
      yPos += 8;
      addText('With 33M+ small businesses in Nigeria alone and low penetration of digital tools, the opportunity for growth is substantial.', 11);
      
      // ===== FUNDING ASK =====
      addPage();
      addTitle('Investment Opportunity', 24);
      yPos += 20;
      
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(40, yPos, pageWidth - 80, 35, 5, 5, 'F');
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Raising', pageWidth / 2, yPos + 12, { align: 'center' });
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(99, 102, 241);
      pdf.text(`$${fundingDetails.seeking.toLocaleString()}`, pageWidth / 2, yPos + 28, { align: 'center' });
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      yPos += 50;
      
      addText('Use of Funds:', 14);
      yPos += 10;
      
      fundingDetails.useOfFunds.forEach(item => {
        addText(`• ${item.category}: $${item.amount.toLocaleString()} (${item.percentage}%)`, 11);
        yPos += 4;
      });
      
      yPos += 15;
      addText('Investment Terms:', 14);
      yPos += 8;
      addText('• Instrument: SAFE (Post-money)', 11);
      yPos += 5;
      addText('• Valuation Cap: $5M', 11);
      yPos += 5;
      addText('• Minimum Check: $25K', 11);
      
      // ===== TEAM =====
      addPage();
      addTitle('Founding Team', 24);
      yPos += 20;
      
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(20, yPos, pageWidth - 40, 50, 5, 5, 'F');
      
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(60, 60, 60);
      pdf.text('Pascal Tombere', 35, yPos + 18);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(99, 102, 241);
      pdf.text('Founder & CEO', 35, yPos + 28);
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      const teamDesc = pdf.splitTextToSize('Entrepreneur with experience in building digital products for African markets. Passionate about simplifying technology for small business owners.', pageWidth - 60);
      pdf.text(teamDesc, 35, yPos + 40);
      
      yPos += 70;
      
      // ===== CONTACT =====
      addTitle('Contact Us', 18);
      yPos += 15;
      addText('Email: investors@bravaxforms.com', 12, 'center');
      yPos += 8;
      addText('Website: www.bravaxforms.com', 12, 'center');
      
      // Save PDF
      pdf.save('Bravax_Forms_Pitch_Deck.pdf');
      
      toast({
        title: "Pitch deck generated!",
        description: "Your investor pitch deck PDF has been downloaded.",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error generating PDF",
        description: "There was an error generating the pitch deck. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <DashboardLayout>
      {/* noindex meta tag to prevent crawling */}
      <Helmet>
        <title>Investor Dataroom | Bravax Forms</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>

      <div className="p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
              <Shield className="h-8 w-8 text-primary" />
              Investor Dataroom
            </h1>
            <p className="text-muted-foreground mt-1">
              Confidential information for potential investors and partners
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={generatePitchDeckPDF}
              disabled={isGeneratingPDF}
              className="gap-2"
            >
              {isGeneratingPDF ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Printer className="h-4 w-4" />
                  Export Pitch Deck
                </>
              )}
            </Button>
            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
              <Lock className="h-3 w-3" />
              Confidential
            </Badge>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-elegant">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Users</p>
                  <p className="text-2xl font-bold text-foreground">{metrics.activeUsers}</p>
                </div>
                <Users className="h-8 w-8 text-primary/50" />
              </div>
              <div className="mt-2 flex items-center gap-1 text-sm text-green-600">
                <TrendingUp className="h-3 w-3" />
                +{metrics.monthlyGrowth}% this month
              </div>
            </CardContent>
          </Card>

          <Card className="card-elegant">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">MRR</p>
                  <p className="text-2xl font-bold text-foreground">${metrics.mrr.toLocaleString()}</p>
                </div>
                <DollarSign className="h-8 w-8 text-primary/50" />
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                ARR: ${metrics.arr.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card className="card-elegant">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Submissions</p>
                  <p className="text-2xl font-bold text-foreground">{metrics.totalSubmissions.toLocaleString()}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-primary/50" />
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {metrics.totalForms.toLocaleString()} forms created
              </div>
            </CardContent>
          </Card>

          <Card className="card-elegant">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Churn Rate</p>
                  <p className="text-2xl font-bold text-foreground">{metrics.churnRate}%</p>
                </div>
                <Target className="h-8 w-8 text-primary/50" />
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                ARPU: ${metrics.avgRevenuePerUser}/mo
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto">
            <TabsTrigger value="overview" className="py-2">Overview</TabsTrigger>
            <TabsTrigger value="documents" className="py-2">Documents</TabsTrigger>
            <TabsTrigger value="funding" className="py-2">Funding</TabsTrigger>
            <TabsTrigger value="team" className="py-2">Team</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Company Summary */}
              <Card className="card-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    Company Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold">What We Do</h4>
                    <p className="text-sm text-muted-foreground">
                      Bravax Forms is an AI-powered form builder and CRM that enables small businesses to collect leads 
                      and manage customer data effortlessly. We help African entrepreneurs scale their sales 
                      process with smart automation and powerful insights.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold">The Problem</h4>
                    <p className="text-sm text-muted-foreground">
                      Small businesses lose hours daily managing fragmented data across multiple apps and chat platforms. 
                      This fragmentation leads to errors, missed leads, and poor customer retention.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold">Our Solution</h4>
                    <p className="text-sm text-muted-foreground">
                      An AI-first form builder and CRM that centralizes data capture and customer management. 
                      Includes deep analytics, automation, and multi-channel delivery features.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Traction Highlights */}
              <Card className="card-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Traction Highlights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium">User Growth</p>
                        <p className="text-sm text-muted-foreground">Month-over-month</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-green-600">+18.5%</p>
                        <p className="text-xs text-muted-foreground">avg. last 6 months</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium">Revenue Growth</p>
                        <p className="text-sm text-muted-foreground">Month-over-month</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-green-600">+22%</p>
                        <p className="text-xs text-muted-foreground">avg. last 6 months</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium">Net Promoter Score</p>
                        <p className="text-sm text-muted-foreground">Customer satisfaction</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-primary">72</p>
                        <p className="text-xs text-muted-foreground">Excellent</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium">Customer LTV</p>
                        <p className="text-sm text-muted-foreground">Lifetime value</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-primary">$288</p>
                        <p className="text-xs text-muted-foreground">12-month avg.</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Market Opportunity */}
              <Card className="card-elegant lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-primary" />
                    Market Opportunity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-3xl font-bold text-primary">$12B</p>
                      <p className="text-sm text-muted-foreground mt-1">TAM</p>
                      <p className="text-xs text-muted-foreground">African SMB Software Market</p>
                    </div>
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-3xl font-bold text-primary">$2.4B</p>
                      <p className="text-sm text-muted-foreground mt-1">SAM</p>
                      <p className="text-xs text-muted-foreground">SMB CRM & Engagement Tools</p>
                    </div>
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-3xl font-bold text-primary">$180M</p>
                      <p className="text-sm text-muted-foreground mt-1">SOM</p>
                      <p className="text-xs text-muted-foreground">Digital CRM & Forms Market</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    With 33M+ small businesses in Nigeria alone and low penetration of professional CRM tools, 
                    the opportunity to modernize how African businesses interact with customers is substantial.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            {documents.map((category, index) => (
              <Card key={index} className="card-elegant">
                <CardHeader>
                  <CardTitle className="text-lg">{category.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {category.items.map((doc, docIndex) => (
                      <div 
                        key={docIndex}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {doc.type} • {doc.size} • Updated {doc.date}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.restricted && (
                            <Badge variant="secondary" className="text-xs">
                              <Lock className="h-3 w-3 mr-1" />
                              Restricted
                            </Badge>
                          )}
                          <Button variant="ghost" size="sm" disabled={doc.restricted}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <Button variant="ghost" size="sm" disabled={doc.restricted}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            <Card className="card-elegant border-primary/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Lock className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">Need access to restricted documents?</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Some documents require NDA or verification. Contact us to request access.
                    </p>
                    <Button onClick={() => window.location.href = 'mailto:investors@bravaxforms.com'}>
                      <Mail className="h-4 w-4 mr-2" />
                      Request Access
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Funding Tab */}
          <TabsContent value="funding" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Funding Ask */}
              <Card className="card-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Funding Round
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center p-6 bg-primary/10 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Raising</p>
                    <p className="text-4xl font-bold text-primary">
                      ${fundingDetails.seeking.toLocaleString()}
                    </p>
                    <Badge variant="secondary" className="mt-2">{fundingDetails.type}</Badge>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold">Use of Funds</h4>
                    {fundingDetails.useOfFunds.map((item, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{item.category}</span>
                          <span className="font-medium">${item.amount.toLocaleString()} ({item.percentage}%)</span>
                        </div>
                        <Progress value={item.percentage} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Milestones */}
              <Card className="card-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Key Milestones
                  </CardTitle>
                  <CardDescription>What we plan to achieve with this funding</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {fundingDetails.milestones.map((milestone, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                        <div className="p-2 bg-primary/10 rounded-full">
                          <Zap className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{milestone.target}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{milestone.timeline}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Investment Terms */}
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle>Investment Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Instrument</p>
                    <p className="text-xl font-bold">SAFE</p>
                    <p className="text-xs text-muted-foreground">Post-money</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Valuation Cap</p>
                    <p className="text-xl font-bold">$5M</p>
                    <p className="text-xs text-muted-foreground">Post-money</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Minimum Check</p>
                    <p className="text-xl font-bold">$25K</p>
                    <p className="text-xs text-muted-foreground">For angels</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-6">
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Founding Team
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-primary">PT</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">Pascal Tombere</h3>
                        <p className="text-sm text-primary">Founder & CEO</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">
                      Entrepreneur with experience in building digital products for African markets. 
                      Passionate about simplifying technology for small business owners.
                    </p>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => window.open('https://linkedin.com', '_blank')}>
                        <ExternalLink className="h-3 w-3 mr-1" />
                        LinkedIn
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact for Investment */}
            <Card className="card-elegant bg-gradient-hero border-primary/20">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="p-3 bg-primary/10 rounded-full w-fit mx-auto">
                    <MessageSquare className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold">Interested in Investing?</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    We're actively raising and would love to discuss how Bravax Forms is 
                    transforming small business commerce in Africa.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={() => window.location.href = 'mailto:investors@bravaxforms.com'}>
                      <Mail className="h-4 w-4 mr-2" />
                      Contact for Investment
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/blog')}>
                      Learn Our Story
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Dataroom;